import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App'
import { supabase } from './lib/supabase'
import { hydrateFromSharedCookie } from './lib/hydrateSession'
import type { Session } from '@supabase/supabase-js'
import { initErrorMonitoring } from './lib/errorMonitoring'
import { identifyUser, initAnalytics, trackDay2ReturnOnBoot, trackReminderOpenedOnBoot } from './lib/analytics'

// Capture uncaught errors and unhandled rejections from the very first tick,
// before any React render can throw.
initErrorMonitoring()

// Decide whether this visit is measured (production, a person, not opted out)
// and start Google Analytics if so — see lib/analytics.ts.
initAnalytics()
// A landing from the daily study-plan email — read off the URL now, before the
// router or a redirect can drop its query (lib/learningSignals.ts).
trackReminderOpenedOnBoot()

async function bootstrap() {
  // Detect email confirmation before getSession() strips the hash.
  //
  // Both flows have to be checked. The implicit flow puts `type=signup` in the
  // hash; the PKCE flow (what `Auth.tsx` asks for, via `emailRedirectTo`) sends
  // the user to `/auth/callback?code=…&type=signup`, with nothing in the hash
  // at all — so checking only the hash meant a confirmed-by-email signup, the
  // normal path, never got the welcome modal.
  const hash = window.location.hash
  const query = new URLSearchParams(window.location.search)
  if (hash.includes('type=signup') || query.get('type') === 'signup') {
    sessionStorage.setItem('show_welcome', '1')
  }

  // getSession() automatically handles OAuth callbacks (PKCE code exchange /
  // implicit-flow hash tokens) and cleans up the URL before we render.
  const { data } = await supabase.auth.getSession()
  let initialSession: Session | null = data.session
  if (!initialSession) {
    await hydrateFromSharedCookie()
    const { data: refreshed } = await supabase.auth.getSession()
    initialSession = refreshed.session
  }

  // The restored account, before the first page view goes out; then the
  // day2_return activation signal, once the session is resolved.
  identifyUser(initialSession?.user ?? null)
  trackDay2ReturnOnBoot()

  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('Root element #root not found in index.html')
  createRoot(rootElement).render(
    <React.StrictMode>
      <App initialSession={initialSession} />
    </React.StrictMode>
  )
}

bootstrap()
